export class SubtitlesPlugin {
    private player: any;
    private subsrc: string;
    private subtitleButtonContainer: HTMLElement;
    private textTracks: HTMLElement;
    private subtitleText: HTMLElement;
    private subtitleContainer: HTMLElement;
    private subtitleView: HTMLElement;
    private subtitle: any;
    private subtitles: any;
    private subcount: number;

    constructor(player: any, subsrc: string) {
        this.player = player;
        this.subsrc = subsrc;
        this.subcount = 0;
    }

    public initPlugin(): void {
        this.initSubtitleContainer();

        try {
            const sub: Promise<void> = this.loadSubtitle();
            sub.then((response: any) => {
                if (response) {
                    this.initSubtitleToggleButton();
                    this.toggleSubtitle();
                }
            });
        } catch (exception) {}
    }

    private initSubtitleContainer(): void {
        this.textTracks = document.getElementsByClassName('vjs-text-track-display')[0] as HTMLElement;

        this.subtitleView = document.createElement('div');
        this.subtitleView.classList.add('vjs-subtitles__subtitle-view');

        this.subtitleContainer = document.createElement('div');
        this.subtitleContainer.classList.add('vjs-subtitles__subtitle-container');

        this.subtitleText = document.createElement('div');
        this.subtitleText.classList.add('vjs-subtitles__subtitle-text');
        this.subtitleText.style.whiteSpace = 'pre-line';
        this.subtitleText.style.lineHeight = '1.1';
    }

    private initSubtitleToggleButton(): void {
        this.subtitleButtonContainer = document.createElement('div');
        this.subtitleButtonContainer.id = 'captions';
        this.subtitleButtonContainer.classList.add('vjs-subs-caps-button', 'vjs-control', 'vjs-button');

        const subtitlebutton: HTMLElement = document.createElement('button');
        subtitlebutton.classList.add('vjs-button');
        subtitlebutton.onclick = this.onButtonClick.bind(this);

        const subtitlebuttonspan: HTMLElement = document.createElement('span');
        subtitlebuttonspan.classList.add('vjs-icon-placeholder');

        subtitlebutton.append(subtitlebuttonspan);
        this.subtitleButtonContainer.append(subtitlebutton);

        document.getElementsByClassName('vjs-control-bar')[0].append(this.subtitleButtonContainer);
    }

    private onButtonClick(event: MouseEvent): void {
        event.preventDefault();
        this.toggleSubtitle();
    }

    private toggleSubtitle(): void {
        const trackStyle: CSSStyleDeclaration = this.textTracks.style;

        if (trackStyle.display === '' || trackStyle.display === 'none') {
            trackStyle.display = 'block';
            this.subtitleButtonContainer.style.color = 'white';
        } else {
            trackStyle.display = 'none';
            this.subtitleButtonContainer.style.color = 'grey';
        }
    }

    public toggleFullscreen(): void {
        if (this.player.isFullscreen()) {
            this.subtitleText.classList.add('fullscreen');
        } else {
            this.subtitleText.classList.remove('fullscreen');
        }
        this.initEvents();
        this.updatePosition();
        this.setSubtitleText();
    }

    private init(): void {
        const texttrack: Element = document.getElementsByClassName('vjs-text-track-display')[0];

        this.subtitleContainer.appendChild(this.subtitleText);
        this.subtitleView.appendChild(this.subtitleContainer);
        if (texttrack.childElementCount > 0) {
            const child: Element = texttrack.children[0];
            texttrack.removeChild(child);
        }
        texttrack.appendChild(this.subtitleView);
    }

    public async loadSubtitle(): Promise<void> {
        // detect media element track support in browser via the existence of the addtrack method
        const video = document.getElementsByTagName('video')[0];
        //const tracksupport = typeof video.addTextTrack == "function" ? true : false;	// check for track element method, if it doesn't exist, the browser generally doesn't support track elements

        if (this.subsrc.indexOf('.srt') !== -1) {
            // we have a track tag and it's a .srt file
            this.init();

            try {
                const response: Response = await fetch(this.subsrc);
                if (response.status === 200) {
                    const sub: string = await response.text();
                    this.subtitle = 'srt';

                    this.subtitles = [];
                    const records: string[] = sub.split(/\n\s*\n/);
                    for (let r = 0; r < records.length; r++) {
                        const record: string = records[r];
                        this.subtitles[r] = [];
                        this.subtitles[r] = record.split(/\n/);
                    }

                    this.initEvents();
                } else {
                    this.subtitle = false;
                }
            } catch (error) {
                this.subtitle = false;
            }
        } else {
            //Add track for vtt to use native vtt support
            const track: HTMLTrackElement = document.createElement('track');
            track.kind = 'subtitle';
            track.label = 'English';
            track.srclang = 'en';
            track.src = this.subsrc;

            video.appendChild(track);

            this.subtitle = 'vtt';
        }

        return this.subtitle;
    }

    private initEvents(): void {
        const that: this = this;

        this.player.on('play', this.updatePosition);
        this.player.on('ended', this.updatePosition);
        this.player.on('seeked', this.updatePosition);

        // add event handler to be called while video is playing
        this.player.on('timeupdate', () => {
            that.updatePosition();
            that.setSubtitleText();
        });

        this.player.on('fullscreenchange', () => {
            this.toggleFullscreen();
        });
    }

    // update position depending on user actions
    private updatePosition(): void {
        if (typeof this.subtitles !== 'undefined') {
            this.subcount = 0;
            while (this.subtitleTimecodeMax(this.subtitles[this.subcount][1]) < this.player.currentTime().toFixed(1)) {
                this.subcount++;
                if (this.subcount > this.subtitles.length - 1) {
                    this.subcount = this.subtitles.length - 1;
                    break;
                }
            }
        }
    }

    private setSubtitleText(): void {
        if (typeof this.subtitles !== 'undefined') {
            let txt = '';
            // check if the next subtitle is in the current time range
            if (
                this.player.currentTime().toFixed(1) > this.subtitleTimecodeMin(this.subtitles[this.subcount][1]) &&
                this.player.currentTime().toFixed(1) < this.subtitleTimecodeMax(this.subtitles[this.subcount][1])
            ) {
                // a subtitle element countains metadata on the first
                // two lines (index and timing information); we skip
                // those two lines and display the rest
                const full: string[] = this.subtitles[this.subcount];
                const text: string[] = full.slice(2, full.length);
                txt = text.join('\n');
            }
            // is there a next timecode?
            if (
                this.player.currentTime().toFixed(1) > this.subtitleTimecodeMax(this.subtitles[this.subcount][1]) &&
                this.subcount < this.subtitles.length - 1
            ) {
                this.subcount++;
            }

            const texttrack: Element = document.getElementsByClassName('vjs-text-track-display')[0];
            if (texttrack.children.item(0) !== this.subtitleView) {
                this.init();
            }

            // update subtitle div
            if (this.subtitleText.innerText !== txt) {
                this.subtitleText.innerText = txt;
            }
        }
    }

    private subtitleTimecodeMin(tc: string): number {
        const tcpair: string[] = tc.split(' --> ');
        return this.subtitleTcSecs(tcpair[0]);
    }

    private subtitleTimecodeMax(tc: string): number {
        const tcpair: string[] = tc.split(' --> ');
        return this.subtitleTcSecs(tcpair[1]);
    }

    private subtitleTcSecs(tc: string): number {
        const tc1: string[] = tc.split(',');
        const tc2: number[] = tc1[0].split(':').map(Number);
        const secs: number = Math.floor(tc2[0] * 60 * 60) + Math.floor(tc2[1] * 60) + Math.floor(tc2[2]);
        return secs;
    }
}

export default SubtitlesPlugin;
